import { Component } from '@theme/component';

const HEIGHT_RESIZE_DEBOUNCE_MS = 200;
const MOBILE_MEDIA_QUERY = '(max-width: 749px)';

/**
 * @typedef {HTMLElement & { syncMediaPlayback(): void }} InGridContentHost
 */

/** @type {Set<InGridContentHost>} */
const instances = new Set();

/** @type {number | undefined} */
let resizeTimeout;

const mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY);

/**
 * @param {Element} grid
 * @returns {number}
 */
const measureShortestProductHeight = (grid) => {
  const productItems = grid.querySelectorAll(':scope > .product-grid__item:not(.in-grid-content__item)');
  let shortest = Infinity;
  let counted = 0;

  for (const productItem of productItems) {
    if (counted >= 10) break;
    if (!(productItem instanceof HTMLElement)) continue;

    const height = productItem.getBoundingClientRect().height;
    counted += 1;
    if (height > 0 && height < shortest) shortest = height;
  }

  return shortest;
};

const updateSoloRowHeights = () => {
  /** @type {Set<HTMLElement>} */
  const grids = new Set();

  for (const instance of instances) {
    const grid = instance.closest('.product-grid');
    if (grid instanceof HTMLElement) grids.add(grid);
  }

  for (const grid of grids) {
    const gap = Number.parseFloat(getComputedStyle(grid).columnGap) || 0;
    const gridWidth = grid.getBoundingClientRect().width;
    const shortest = measureShortestProductHeight(grid);

    for (const item of grid.querySelectorAll(':scope > .in-grid-content__item')) {
      if (!(item instanceof HTMLElement)) continue;

      const isFullRow = Math.abs(item.getBoundingClientRect().width - gridWidth) <= gap + 1;

      if (!isFullRow || shortest === Infinity) {
        item.style.removeProperty('--in-grid-content-height');
        continue;
      }

      item.style.setProperty('--in-grid-content-height', `${shortest}px`);
    }
  }
};

const syncAllMediaPlayback = () => {
  for (const instance of instances) {
    instance.syncMediaPlayback();
  }
};

const handleResize = () => {
  window.clearTimeout(resizeTimeout);
  resizeTimeout = window.setTimeout(updateSoloRowHeights, HEIGHT_RESIZE_DEBOUNCE_MS);
};

/**
 * @param {InGridContentHost} instance
 */
const registerInstance = (instance) => {
  instances.add(instance);

  if (instances.size === 1) {
    window.addEventListener('resize', handleResize);
    mobileQuery.addEventListener('change', syncAllMediaPlayback);

    if (document.readyState !== 'complete') {
      window.addEventListener(
        'load',
        () => {
          updateSoloRowHeights();
          syncAllMediaPlayback();
        },
        { once: true }
      );
      return;
    }
  }

  if (document.readyState === 'complete') {
    updateSoloRowHeights();
  }
};

/**
 * @param {InGridContentHost} instance
 */
const unregisterInstance = (instance) => {
  instances.delete(instance);
  if (instances.size > 0) return;

  window.removeEventListener('resize', handleResize);
  mobileQuery.removeEventListener('change', syncAllMediaPlayback);
  window.clearTimeout(resizeTimeout);
};

/**
 * Play/pause for in-grid video, and height matching when the card sits on its own row.
 *
 * @extends {Component}
 */
class InGridContentComponent extends Component {
  /** @type {ResizeObserver | undefined} */
  #overlayTextObserver;

  /** @type {WeakSet<HTMLVideoElement>} */
  #userPausedVideos = new WeakSet();

  connectedCallback() {
    super.connectedCallback();

    registerInstance(this);
    this.syncMediaPlayback();
    this.#observeOverlayText();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    unregisterInstance(this);
    this.#overlayTextObserver?.disconnect();
  }

  /**
   * Overlay <a> can intercept the press; stop it before navigation starts.
   *
   * @param {Event} event
   */
  handleVideoPointerDown(event) {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * @param {Event} event
   */
  toggleVideo(event) {
    event.preventDefault();
    event.stopPropagation();

    const toggle = event.target;
    if (!(toggle instanceof HTMLElement)) return;

    const media = toggle.closest('.in-grid-content__media');
    if (!(media instanceof HTMLElement)) return;

    const video = media.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    if (video.paused) {
      this.#userPausedVideos.delete(video);
      video.play();
      this.#updateVideoToggle(media, true);
      return;
    }

    this.#userPausedVideos.add(video);
    video.pause();
    this.#updateVideoToggle(media, false);
  }

  syncMediaPlayback() {
    const item = this.closest('.in-grid-content__item');
    const itemHidden = item instanceof HTMLElement && getComputedStyle(item).display === 'none';
    const mediaStacks = this.querySelectorAll('.in-grid-content__media');

    for (const media of mediaStacks) {
      if (!(media instanceof HTMLElement)) continue;

      const video = media.querySelector('video');
      if (!(video instanceof HTMLVideoElement)) continue;

      const isHidden = itemHidden || getComputedStyle(media).display === 'none';

      if (isHidden) {
        video.pause();
        this.#updateVideoToggle(media, false);
        continue;
      }

      if (video.paused && video.autoplay && !this.#userPausedVideos.has(video)) {
        video.play();
        this.#updateVideoToggle(media, true);
      }
    }
  }

  /**
   * @param {HTMLElement} media
   * @param {boolean} isPlaying
   */
  #updateVideoToggle(media, isPlaying) {
    media.querySelector('.in-grid-content__video-icon--play')?.classList.toggle('hidden', isPlaying);
    media.querySelector('.in-grid-content__video-icon--pause')?.classList.toggle('hidden', !isPlaying);
  }

  #observeOverlayText() {
    if (!this.classList.contains('in-grid-content--overlay')) return;

    this.#syncOverlayTextOffset();

    const text = this.querySelector('.in-grid-content__text');
    if (!(text instanceof HTMLElement)) return;

    this.#overlayTextObserver = new ResizeObserver(() => {
      this.#syncOverlayTextOffset();
    });
    this.#overlayTextObserver.observe(text);
  }

  #syncOverlayTextOffset() {
    const text = this.querySelector('.in-grid-content__text');
    const offset = text instanceof HTMLElement ? text.getBoundingClientRect().height : 0;
    this.style.setProperty('--in-grid-overlay-text-offset', `${offset}px`);
  }
}

if (!customElements.get('in-grid-content-component')) {
  customElements.define('in-grid-content-component', InGridContentComponent);
}
